<script setup lang="ts">
import { computed } from 'vue';

export interface Picture {
  sources: {
    srcset: { path: string; size?: string }[] | string;
    width: number;
    height: number;
    media?: string;
  }[];
  image: {
    src: string;
    srcset?: string;
    width: number;
    height: number;
  };
}

export interface PhxPictureContentProps {
  alt?: string;
  title?: string;
  image?: Picture['image'];
  imgClass?: string;
  lazy?: boolean;
  lazyPlaceholder?: boolean;
  sources?: Picture['sources'];
}

const { lazy, lazyPlaceholder, image } = defineProps<PhxPictureContentProps>();

const emit = defineEmits<(e: 'load', ev: Event) => void>();

const loadEvent = computed(() => (lazy ? 'lazyloaded' : 'load'));

function placeholder(width?: number, height?: number): string | null {
  if (!lazyPlaceholder || !width || !height) return null;
  return `data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width=%22${width}%22%20height=%22${height}%22%20viewBox%3D%220%200%20${width}%20${height}%22%3E%3C%2Fsvg%3E`;
}

function concatSrcSet(srcset: Record<string, string>[] | string = []): string {
  if (typeof srcset === 'string') {
    return srcset;
  }
  return srcset.map((entry) => (entry.size ? `${entry.path} ${entry.size}` : entry.path)).join(', ');
}

function getSourceAttrs(source: Picture['sources'][0]) {
  const dimensions = {
    ...(source.width != null ? { width: source.width } : {}),
    ...(source.height != null ? { height: source.height } : {}),
  };

  if (lazy) {
    const placeholderSrc = placeholder(source.width, source.height);
    return {
      ...dimensions,
      ...(placeholderSrc ? { srcset: placeholderSrc } : {}),
      'data-srcset': concatSrcSet(source.srcset),
    };
  }

  return {
    ...dimensions,
    srcset: concatSrcSet(source.srcset),
  };
}

function getImgAttrs() {
  if (lazy) {
    const placeholderSrc = placeholder(image?.width, image?.height);
    return {
      ...(placeholderSrc ? { src: placeholderSrc } : {}),
      ...(image?.src ? { 'data-src': image.src } : {}),
      ...(image?.srcset ? { 'data-srcset': image.srcset } : {}),
    };
  }
  return {
    ...(image?.src ? { src: image.src } : {}),
    ...(image?.srcset ? { srcset: image.srcset } : {}),
  };
}

function handleLoaded(e: Event) {
  emit('load', e);
}
</script>

<template>
  <picture>
    <source
      v-for="source in sources"
      :key="source.media || concatSrcSet(source.srcset)"
      :media="source.media"
      v-bind="getSourceAttrs(source)"
    />

    <img
      :class="[imgClass, { 'phxsg-lazyload phxsg-lazyload--fade': lazy }]"
      :alt="alt"
      :title="title"
      v-bind="getImgAttrs()"
      :width="image?.width"
      :height="image?.height"
      @[loadEvent]="handleLoaded"
    />
  </picture>
</template>
